"use client";

import { useState, useRef, useEffect, useLayoutEffect, useTransition } from "react";
import { createPortal } from "react-dom";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/lib/i18n/navigation";
import { locales, localeLabels, type Locale } from "@/lib/i18n/config";
import { cn } from "@/lib/utils";

const FLAGS: Record<Locale, string> = {
  fr: "🇫🇷",
  ar: "🇲🇦",
  en: "🇬🇧",
};

const LABELS_NATIVE: Record<Locale, string> = {
  fr: "Français",
  ar: "العربية",
  en: "English",
};

const LABELS_SHORT: Record<Locale, string> = {
  fr: "FR",
  ar: "AR",
  en: "EN",
};

export function LocaleSwitcher({
  variant = "default",
}: {
  variant?: "default" | "compact" | "ghost" | "hero" | "footer";
}) {
  const current = useLocale() as Locale;
  const t = useTranslations("common.localeSwitcher");
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [mounted, setMounted] = useState(false);
  // Position du dropdown rendu en portal (document.body).
  //
  // Stratégie : `position: absolute` + coordonnées PAGE (top = r.top + scrollY).
  // Le portal cible <body> qui n'a aucun ancêtre transformé, donc `absolute`
  // se réfère à l'initial containing block ≈ document. Coordonnées page
  // robustes peu importe le scroll.
  //
  // Pourquoi PAS `position: fixed` ? Sur cette page, un ancêtre du portal
  // (probablement le hero avec backdrop-blur / transform-gpu) crée un
  // containing block qui piège `position: fixed` — le dropdown se
  // retrouve positionné par rapport au transform context au lieu du
  // viewport, d'où le menu qui apparaissait à ~600px sous le trigger
  // au lieu de juste en dessous (bug 2026-05-15 v2).
  const [portalPos, setPortalPos] = useState<{
    top: number;
    rightFromBody: number;
    leftFromBody: number;
  } | null>(null);

  useEffect(() => setMounted(true), []);

  useLayoutEffect(() => {
    if (!open) return;
    function recalc() {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPortalPos({
        // PAGE coordinates (= viewport + scroll) — compatibles avec absolute.
        top: r.bottom + scrollY + 8,
        // right depuis le bord droit de la page (document, pas viewport).
        rightFromBody: document.documentElement.clientWidth - r.right - scrollX,
        leftFromBody: r.left + scrollX,
      });
    }
    recalc();
    window.addEventListener("scroll", recalc, true);
    window.addEventListener("resize", recalc);
    return () => {
      window.removeEventListener("scroll", recalc, true);
      window.removeEventListener("resize", recalc);
    };
  }, [open]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        ref.current && !ref.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  function pickLocale(loc: Locale) {
    setOpen(false);
    startTransition(() => {
      router.replace(pathname, { locale: loc });
    });
  }

  const isHero = variant === "hero";
  const isFooter = variant === "footer";

  // ── Variante "hero" : pill compact code locale (FR/AR/EN), glassmorphism, h-11 ──
  // Le dropdown est rendu via createPortal sur document.body pour échapper aux
  // stacking contexts parents (header sticky vs hero backdrop-blur). Sans ça
  // le menu se retrouvait caché derrière la SearchBar du hero.
  if (isHero) {
    const isRtl = current === "ar";
    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "inline-flex h-11 min-w-[3.25rem] items-center justify-center gap-1.5 rounded-full px-3 text-[13px] font-bold tracking-wide text-white transition-all duration-150",
            "bg-white/15 backdrop-blur-sm hover:bg-white/25 active:scale-[0.96]",
            "focus-visible:outline-2 focus-visible:outline-white/60 focus-visible:outline-offset-2",
            open && "bg-white/25",
            isPending && "opacity-60",
          )}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={t("currentLanguage", { label: localeLabels[current] })}
        >
          <span>{LABELS_SHORT[current]}</span>
          <svg
            className={cn("h-3 w-3 opacity-90 transition-transform duration-200", open && "rotate-180")}
            viewBox="0 0 12 12"
            aria-hidden
          >
            <path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {mounted && open && portalPos && createPortal(
          <>
            {/* Backdrop fullscreen — fixed est OK ici (couvre tout, pas d'ancrage précis) */}
            <div
              aria-hidden
              className="fixed inset-0 z-[9998]"
              onClick={() => setOpen(false)}
            />
            {/* Dropdown : position: absolute + coords PAGE — robuste contre les
                ancêtres transformés qui piégeraient `fixed`. */}
            <div
              ref={ref}
              role="menu"
              style={{
                position: "absolute",
                top: portalPos.top,
                ...(isRtl
                  ? { left: portalPos.leftFromBody }
                  : { right: portalPos.rightFromBody }),
              }}
              className={cn(
                "z-[9999]",
                "flex flex-col gap-1.5 rounded-2xl border border-[var(--sahha-border-subtle)] bg-white p-1.5",
                "shadow-[0_16px_40px_-8px_rgba(15,30,58,0.25)]",
                "origin-top-right rtl:origin-top-left animate-in fade-in-0 zoom-in-95 duration-150",
              )}
            >
              {locales.map((loc) => {
                const active = loc === current;
                return (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => pickLocale(loc)}
                    role="menuitemradio"
                    aria-checked={active}
                    aria-label={LABELS_NATIVE[loc]}
                    className={cn(
                      "inline-flex h-9 min-w-[3.5rem] items-center justify-center rounded-full px-3 text-[12.5px] font-bold tracking-wide transition-all duration-150 active:scale-[0.96]",
                      active
                        ? "bg-[var(--sahha-primary-600)] text-white shadow-sm"
                        : "bg-[var(--sahha-surface-1)] text-[var(--sahha-text-700)] hover:bg-[var(--sahha-primary-50)] hover:text-[var(--sahha-primary-700)]",
                    )}
                  >
                    {LABELS_SHORT[loc]}
                  </button>
                );
              })}
            </div>
          </>,
          document.body,
        )}
      </>
    );
  }

  // ── Variante "footer" : pill native-app style, drapeau + nom natif + chevron, dropdown bottom-up ──
  if (isFooter) {
    return (
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "inline-flex h-10 items-center gap-2.5 rounded-full border border-[var(--sahha-border-default)] bg-white px-4 text-[13px] font-semibold text-[var(--sahha-text-800)]",
            "shadow-[0_2px_8px_-2px_rgba(15,30,58,0.08)] transition-all duration-150",
            "hover:border-[var(--sahha-primary-300)] hover:bg-[var(--sahha-primary-50)]/50 active:scale-[0.97]",
            "focus-visible:outline-2 focus-visible:outline-[var(--sahha-primary-300)] focus-visible:outline-offset-2",
            isPending && "opacity-60",
          )}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={t("currentLanguage", { label: localeLabels[current] })}
        >
          <span aria-hidden className="text-base leading-none">{FLAGS[current]}</span>
          <span>{LABELS_NATIVE[current]}</span>
          <svg
            className={cn("h-3 w-3 text-[var(--sahha-text-500)] transition-transform duration-200", open && "rotate-180")}
            viewBox="0 0 12 12"
            aria-hidden
          >
            <path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {open && (
          <>
            <div aria-hidden className="fixed inset-0 z-[var(--z-popover-backdrop,40)] sm:hidden" onClick={() => setOpen(false)} />
            <div
              role="menu"
              className={cn(
                "absolute left-1/2 -translate-x-1/2 bottom-[calc(100%+8px)] z-[var(--z-popover)] w-40 overflow-hidden rounded-2xl border border-[var(--sahha-border-default)] bg-white p-1 shadow-[0_16px_40px_-8px_rgba(15,30,58,0.22)]",
                "origin-bottom animate-in fade-in-0 zoom-in-95 duration-150",
              )}
            >
              {locales.map((loc) => {
                const active = loc === current;
                return (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => pickLocale(loc)}
                    role="menuitemradio"
                    aria-checked={active}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-start text-[13px] transition-colors",
                      active
                        ? "bg-[var(--sahha-primary-50)] font-semibold text-[var(--sahha-primary-800)]"
                        : "text-[var(--sahha-text-700)] hover:bg-[var(--sahha-surface-1)]",
                    )}
                  >
                    <span aria-hidden className="text-base leading-none">{FLAGS[loc]}</span>
                    <span className="flex-1 truncate">{LABELS_NATIVE[loc]}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Variantes default / compact / ghost (header desktop, etc.) ──
  const triggerClasses = cn(
    "inline-flex items-center gap-2 rounded-full font-medium transition-all duration-150",
    "focus-visible:outline-3 focus-visible:outline-[var(--sahha-primary-300)] focus-visible:outline-offset-2",
    isPending && "opacity-60",
    variant === "compact" && "px-3 py-1.5 text-sm",
    variant === "default" &&
      "border border-[var(--sahha-border-default)] bg-white px-3.5 py-2 text-sm text-[var(--sahha-text-700)] hover:border-[var(--sahha-primary-300)] hover:bg-[var(--sahha-primary-50)]",
    variant === "ghost" &&
      "px-3 py-2 text-sm text-[var(--sahha-text-700)] hover:bg-[var(--sahha-surface-1)]",
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={triggerClasses}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("currentLanguage", { label: localeLabels[current] })}
      >
        <span aria-hidden className="text-base leading-none">
          {FLAGS[current]}
        </span>
        <span className="hidden sm:inline">{current.toUpperCase()}</span>
        <svg
          className={cn("h-3.5 w-3.5 transition-transform duration-200", open && "rotate-180")}
          viewBox="0 0 12 12"
          aria-hidden
        >
          <path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 top-[calc(100%+8px)] z-[var(--z-popover)] w-56 overflow-hidden rounded-2xl border border-[var(--sahha-border-default)] bg-white p-1.5 shadow-[var(--sahha-shadow-xl)]"
        >
          <p className="px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-widest text-[var(--sahha-text-400)]">
            {t("chooseLanguage")}
          </p>
          {locales.map((loc) => {
            const active = loc === current;
            return (
              <button
                key={loc}
                type="button"
                onClick={() => pickLocale(loc)}
                role="menuitemradio"
                aria-checked={active}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm transition-colors",
                  active
                    ? "bg-[var(--sahha-primary-50)] font-medium text-[var(--sahha-primary-800)]"
                    : "text-[var(--sahha-text-700)] hover:bg-[var(--sahha-surface-1)]",
                )}
              >
                <span aria-hidden className="text-lg leading-none">{FLAGS[loc]}</span>
                <span className="flex-1">
                  {LABELS_NATIVE[loc]}
                  <span className="ms-2 text-xs uppercase text-[var(--sahha-text-400)]">{loc}</span>
                </span>
                {active && (
                  <svg className="h-4 w-4 text-[var(--sahha-primary-600)]" viewBox="0 0 16 16" aria-hidden>
                    <path d="M3 8l3.5 3.5L13 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
